import { useMemo } from 'react'

import type { ExerciseMuscle, MuscleSlug } from '@/api/types/workout'
import { cn } from '@/lib/utils'

import { cycleMuscleRole, MUSCLE_ROLE_COLORS, MUSCLE_SLUGS, muscleLabel } from '../../muscle-level'
import { MuscleBodyMap } from './muscle-body-map'

const ROLE_LABELS = new Map<ExerciseMuscle['role'], string>([
  ['primary', '主'],
  ['secondary', '補助'],
])

const sortMuscles = (muscles: ExerciseMuscle[]): ExerciseMuscle[] =>
  [...muscles].sort(
    (a, b) =>
      (a.role === b.role ? 0 : a.role === 'primary' ? -1 : 1) ||
      MUSCLE_SLUGS.indexOf(a.muscle) - MUSCLE_SLUGS.indexOf(b.muscle),
  )

const roleColors = (muscles: ExerciseMuscle[]): Map<MuscleSlug, string> =>
  new Map(muscles.map((m) => [m.muscle, MUSCLE_ROLE_COLORS.get(m.role) ?? '']))

const RoleLegend = () => (
  <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
    {[...ROLE_LABELS].map(([role, label]) => (
      <span key={role} className="flex items-center gap-1">
        <span className="inline-block size-2.5 rounded-sm" style={{ background: MUSCLE_ROLE_COLORS.get(role) }} />
        {label}
      </span>
    ))}
  </div>
)

/** 種目一覧に出す筋群のバッジ（主働筋 → 補助筋） */
export const MuscleBadges = ({ muscles }: { muscles: ExerciseMuscle[] }) => {
  if (muscles.length === 0) return null
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {sortMuscles(muscles).map((m) => (
        <span
          key={m.muscle}
          className={cn(
            'rounded px-1.5 py-0.5 text-[10px] leading-none',
            m.role === 'primary' ? 'bg-red-600/20 text-red-400' : 'bg-red-300/10 text-red-300/80',
          )}
        >
          {muscleLabel(m.muscle)}
        </span>
      ))}
    </div>
  )
}

type PickerProps = {
  value: ExerciseMuscle[]
  onChange: (muscles: ExerciseMuscle[]) => void
}

/** 人体図の筋肉をタップして 主 → 補助 → なし を切り替える */
export const MusclePicker = ({ value, onChange }: PickerProps) => {
  const colors = useMemo(() => roleColors(value), [value])
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">タップで 主 → 補助 → なし</span>
        <RoleLegend />
      </div>
      <MuscleBodyMap
        colors={colors}
        onMuscleClick={(slug) => onChange(cycleMuscleRole(value, slug))}
        className="h-56"
      />
      <MuscleBadges muscles={value} />
    </div>
  )
}
